import { maverickPortalconfig } from '../config/apiConfig';

const multipartApiBase = () =>
  String(maverickPortalconfig.env[0].DEV.hostname || '').replace(/\/$/, '');

const putPart = (uploadUrl, chunk, onPartProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.upload.onprogress = event => {
      if (typeof onPartProgress === 'function' && Number(event.loaded) > 0) {
        onPartProgress(event.loaded);
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = String(xhr.getResponseHeader('ETag') || '').replace(/^"|"$/g, '');
        if (!etag) {
          reject(new Error('Video part upload did not return an ETag'));
          return;
        }
        resolve(etag);
        return;
      }
      reject(new Error(`Video part upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error('Video part upload failed'));
    xhr.send(chunk);
  });

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
});

const createUploadError = (message, { network = false, shouldRetry = false } = {}) => {
  const error = new Error(message);
  error.isNetworkError = network;
  error.shouldRetryUpload = shouldRetry;
  return error;
};

const fetchMultipartJson = async (path, body) => {
  let response;
  try {
    response = await fetch(`${multipartApiBase()}${path}`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
  } catch (error) {
    // Network drop during multipart APIs must NOT set shouldRetryUpload,
    // so the UI can show the 3 connection messages before Retry/Delete.
    throw createUploadError('Network error. Please check your connection and try again.', {
      network: true,
      shouldRetry: false,
    });
  }

  const responseBody = await response.json().catch(() => null);
  return { response, body: responseBody };
};

const isPresignedUrlExpired = uploadUrl => {
  try {
    const parsed = new URL(uploadUrl);
    const expires = Number(parsed.searchParams.get('X-Amz-Expires'));
    const amzDate = parsed.searchParams.get('X-Amz-Date') || '';
    if (!Number.isFinite(expires) || expires <= 0 || amzDate.length < 16) return false;
    const signedAt = Date.parse(
      `${amzDate.slice(0, 4)}-${amzDate.slice(4, 6)}-${amzDate.slice(6, 8)}T${amzDate.slice(9, 11)}:${amzDate.slice(11, 13)}:${amzDate.slice(13, 15)}Z`
    );
    if (!Number.isFinite(signedAt)) return false;
    return Date.now() > signedAt + expires * 1000 - 30000;
  } catch (error) {
    return false;
  }
};

const chunkEnd = (partNumber, partSize, fileSize) =>
  Math.min((partNumber - 1) * partSize + partSize, fileSize);

export const formatRemainingUploadTime = seconds => {
  if (!Number.isFinite(seconds) || seconds < 0) return null;
  const total = Math.max(0, Math.ceil(seconds));
  if (total < 60) return 'Less than a minute remaining';
  const mins = Math.max(1, Math.ceil(total / 60));
  return `approx ${mins} minutes remaining`;
};

/**
 * Starts or resumes a multipart upload, PUTs each unfinished slice,
 * then completes the upload with the ETag from every part.
 *
 * onProgress(fraction, meta) meta.stage:
 * - preparing  -> initiateMultipartUpload
 * - uploading  -> AWS part PUTs (includes remainingSeconds)
 * - confirming -> completeMultipartUpload / CheckFileInS3
 */
export async function uploadVideoInChunks({ file, fileName, onProgress, resumeSession, onSessionUpdate }) {
  const fileSize = file?.size;
  if (!file || !Number.isFinite(fileSize) || fileSize <= 0) {
    throw new Error('Video file is empty.');
  }

  const emitProgress = (fraction, meta = {}) => {
    if (typeof onProgress !== 'function') return;
    onProgress(Math.min(1, Math.max(0, fraction)), meta);
  };

  const saved = resumeSession || {};
  if (saved.status === 'check_pending') {
    emitProgress(1, {
      stage: 'confirming',
      remainingSeconds: null,
      bytesUploaded: fileSize,
      fileSize,
    });
    return saved.completeBody || { status_code: 200, success: true };
  }

  let uploadId = saved.uploadId || saved.upload_id || null;
  let s3Key = saved.s3Key || saved.s3_key || null;
  let partSize = Number(saved.partSize || saved.part_size);
  let parts = Array.isArray(saved.parts) ? [...saved.parts] : [];
  let completedParts = Array.isArray(saved.completedParts) ? [...saved.completedParts] : [];

  const completedNumbers = new Set(completedParts.map(part => Number(part.PartNumber)));
  const unfinishedUrlExpired =
    parts.length > 0 &&
    parts.some(part => {
      const partNumber = Number(part.part_number);
      const start = (partNumber - 1) * partSize;
      if (!Number.isFinite(start) || start >= fileSize || completedNumbers.has(partNumber)) return false;
      return isPresignedUrlExpired(part.upload_url);
    });

  if (unfinishedUrlExpired) {
    uploadId = null;
    s3Key = null;
    partSize = NaN;
    parts = [];
    completedParts = [];
    completedNumbers.clear();
  }

  const needsInitiate =
    !uploadId || !s3Key || !Number.isFinite(partSize) || partSize <= 0 || parts.length === 0;

  if (needsInitiate) {
    emitProgress(0, {
      stage: 'preparing',
      remainingSeconds: null,
      bytesUploaded: 0,
      fileSize,
    });

    const { response, body } = await fetchMultipartJson('/initiateMultipartUpload', {
      file_name: fileName,
      file_size: fileSize,
    });

    if (!response.ok) {
      throw createUploadError('Failed to start video upload. Please try again.');
    }

    const data = body?.data;
    uploadId = data?.upload_id;
    s3Key = data?.s3_key;
    partSize = Number(data?.part_size);
    parts = Array.isArray(data?.parts) ? [...data.parts] : [];
    completedParts = [];
    completedNumbers.clear();

    if (!uploadId || !s3Key || !Number.isFinite(partSize) || partSize <= 0 || parts.length === 0) {
      throw createUploadError('Failed to start video upload. Please try again.');
    }

    if (typeof onSessionUpdate === 'function') {
      await onSessionUpdate({
        uploadId,
        s3Key,
        partSize,
        parts,
        completedParts,
        bytesUploaded: 0,
        status: 'uploading',
      });
    }
  }

  parts.sort((a, b) => Number(a.part_number) - Number(b.part_number));

  let uploadedBytes = 0;
  parts.forEach(part => {
    const partNumber = Number(part.part_number);
    const start = (partNumber - 1) * partSize;
    if (start >= fileSize || !completedNumbers.has(partNumber)) return;
    uploadedBytes += chunkEnd(partNumber, partSize, fileSize) - start;
  });

  // Prefer recent ~2.5s speed; fall back to average since this upload loop started.
  const SPEED_WINDOW_MS = 2500;
  const uploadLoopStartedAt = Date.now();
  const uploadLoopStartedBytes = uploadedBytes;
  const speedSamples = [{ t: uploadLoopStartedAt, bytes: uploadedBytes }];
  let lastRemainingSeconds = null;

  const getCurrentBytesPerSecond = totalUploaded => {
    const now = Date.now();
    speedSamples.push({ t: now, bytes: totalUploaded });
    while (speedSamples.length > 1 && now - speedSamples[0].t > SPEED_WINDOW_MS) {
      speedSamples.shift();
    }

    if (speedSamples.length >= 2) {
      const oldest = speedSamples[0];
      const newest = speedSamples[speedSamples.length - 1];
      const elapsedSec = (newest.t - oldest.t) / 1000;
      const bytesDelta = newest.bytes - oldest.bytes;
      if (elapsedSec >= 0.25 && bytesDelta > 0) {
        return bytesDelta / elapsedSec;
      }
    }

    const overallElapsed = (now - uploadLoopStartedAt) / 1000;
    const overallDelta = totalUploaded - uploadLoopStartedBytes;
    if (overallElapsed >= 0.5 && overallDelta > 0) {
      return overallDelta / overallElapsed;
    }
    return null;
  };

  const report = loadedInPart => {
    const totalUploaded = Math.min(fileSize, uploadedBytes + loadedInPart);
    const fraction = totalUploaded / fileSize;
    let remainingSeconds = lastRemainingSeconds;

    if (totalUploaded >= fileSize) {
      remainingSeconds = 0;
    } else {
      const bytesPerSecond = getCurrentBytesPerSecond(totalUploaded);
      if (bytesPerSecond > 0) {
        remainingSeconds = (fileSize - totalUploaded) / bytesPerSecond;
      }
    }

    lastRemainingSeconds = remainingSeconds;
    emitProgress(Math.min(1, Math.max(0, fraction)), {
      stage: 'uploading',
      remainingSeconds,
      bytesUploaded: totalUploaded,
      fileSize,
    });
  };

  emitProgress(fileSize ? uploadedBytes / fileSize : 0, {
    stage: 'uploading',
    remainingSeconds: null,
    bytesUploaded: uploadedBytes,
    fileSize,
  });

  for (const part of parts) {
    const partNumber = Number(part.part_number);
    const start = (partNumber - 1) * partSize;
    if (start >= fileSize) break;
    if (completedNumbers.has(partNumber)) continue;

    const end = chunkEnd(partNumber, partSize, fileSize);
    const chunk = file.slice(start, end);
    if (!part.upload_url) {
      throw createUploadError('Video upload failed. Please try again.');
    }

    let etag;
    try {
      etag = await putPart(part.upload_url, chunk, report);
    } catch (error) {
      const isNetwork = !error?.message?.includes('ETag');
      throw createUploadError(
        isNetwork
          ? 'Network error while uploading video. Please try again.'
          : 'Video upload failed. Please try again.',
        { network: isNetwork, shouldRetry: false }
      );
    }
    completedParts.push({
      PartNumber: partNumber,
      ETag: etag,
    });
    completedNumbers.add(partNumber);
    uploadedBytes += chunk.size;
    report(0);

    if (typeof onSessionUpdate === 'function') {
      await onSessionUpdate({
        uploadId,
        s3Key,
        partSize,
        parts,
        completedParts: [...completedParts],
        bytesUploaded: uploadedBytes,
        status: 'uploading',
      });
    }
  }

  emitProgress(1, {
    stage: 'confirming',
    remainingSeconds: null,
    bytesUploaded: fileSize,
    fileSize,
  });

  const { response: completeResponse, body: completeBody } = await fetchMultipartJson(
    '/completeMultipartUpload',
    {
      upload_id: uploadId,
      s3_key: s3Key,
      parts: completedParts,
    }
  );
  const statusCode = Number(completeBody?.status_code);

  if (!completeResponse.ok || statusCode !== 200) {
    try {
      await fetchMultipartJson('/abortMultipartUpload', {
        upload_id: uploadId,
        s3_key: s3Key,
      });
    } catch (abortError) {
      // Still surface the complete failure even if abort itself fails.
    }

    if (typeof onSessionUpdate === 'function') {
      await onSessionUpdate({
        uploadId: null,
        s3Key: null,
        partSize: null,
        parts: [],
        completedParts: [],
        bytesUploaded: 0,
        status: 'aborted',
        completeBody: null,
      });
    }

    // Complete API failure: skip the 3 connection messages and go to Retry/Delete.
    throw createUploadError('Video upload failed. Please try again.', { shouldRetry: true });
  }

  if (typeof onSessionUpdate === 'function') {
    await onSessionUpdate({
      uploadId,
      s3Key,
      partSize,
      parts,
      completedParts,
      bytesUploaded: fileSize,
      status: 'check_pending',
      completeBody,
    });
  }

  emitProgress(1, {
    stage: 'confirming',
    remainingSeconds: null,
    bytesUploaded: fileSize,
    fileSize,
  });

  return completeBody;
}
